import React, { useEffect, useState, useRef } from 'react'
import { useIntl } from 'react-intl'
import { get_payment_scheme_filters, get_payment_scheme_filters_types } from '../../../core/_request'
import { FilterI } from '../../../core/_models'
import { getFilterOptionsArr, parseJwt, getDatePlaceholder } from '../../../core/helper'
import CardLoading from '../../../components/CardLoading'
import { usePaymentSchemesQueryRequest } from './core/PaymentSchemesRequestProvider'
import { useAuth } from '../../../../auth/core/Auth'
import Flatpickr from 'react-flatpickr'
import momentTimezone from 'moment-timezone'
import clsx from 'clsx'
import { KTIcon } from '../../../../../../general/helpers'
import { usePaymentSchemesQueryResponse } from './core/PaymentSchemesResponseProvider'




const PaymentSchemesFilterComp = () => {
    const intl = useIntl()
    const [loading, setLoading] = useState<boolean>(false)
    const [filterData, setFilterData] = useState<FilterI[]>([])
    const { updateState, state } = usePaymentSchemesQueryRequest()
    const { isLoading } = usePaymentSchemesQueryResponse()

    // Local state for inputs.
    const [localFilters, setLocalFilters] = useState<{ [key: string]: any }>({})

    // For Date Range picker
    const [selectedDateFrom, setSelectedDateFrom] = useState<Date | undefined>()
    const [selectedDateTo, setSelectedDateTo] = useState<Date | undefined>()

    const { auth } = useAuth()
    const { activeRetailer } = parseJwt(auth?.accessToken ?? '')
    const datePlaceHolder = getDatePlaceholder(activeRetailer?.locale, activeRetailer?.timeZone)

    // Fetch Filter Definitions
    const fetchFilters = async () => {
        setLoading(true)
        try {
            const { data: typesData } = await get_payment_scheme_filters_types(auth?.accessToken)
            // Assuming slug 'payment-schemes' exists or will exist.
            const schemesType = typesData.find((_type) => _type.slug === 'payment-schemes')
            if (schemesType) {
                const { data } = await get_payment_scheme_filters(auth?.accessToken, { type: schemesType._id })
                setFilterData(data.sort((a, b) => a.order - b.order))
            } else {
                // Fallback or empty if not found
                setFilterData([])
            }
        } catch (e) {
            console.error(e)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchFilters()
    }, []) // Fetch on mount

    useEffect(() => {
        const { page, size, ...filterState } = state
        const tempFilters = {}

        Object.entries(filterState).forEach(([key, val]) => {
            let cleanKey = key;
            if (key.includes('.')) {
                cleanKey = key.split('.')[0]
            }
            tempFilters[cleanKey] = val
        })

        setLocalFilters(tempFilters)

        // For Date Range:
        if (state['startDateTime.greaterThanOrEqual']) {
            setSelectedDateFrom(new Date(state['startDateTime.greaterThanOrEqual'] as string))
        } else {
            setSelectedDateFrom(undefined)
        }
        if (state['startDateTime.lessThan']) {
            setSelectedDateTo(new Date(state['startDateTime.lessThan'] as string))
        } else {
            setSelectedDateTo(undefined)
        }
    }, [state])

    const applyFilters = () => {
        const tempGlobalFilters = {}

        Object.entries(localFilters).forEach(([key, val]) => {
            if (!val) return;

            let finalKey = key;

            if (key === 'name' || key === 'ecrTenderTypeCode') {
                finalKey = `${key}.contains`
            } else if (key !== 'startDateTime') {
                finalKey = `${key}.equals`
            }

            if (key !== 'startDateTime') {
                tempGlobalFilters[finalKey] = val
            }
        })

        // Add dates
        if (selectedDateFrom) {
            tempGlobalFilters[`startDateTime.greaterThanOrEqual`] = momentTimezone(selectedDateFrom).tz(activeRetailer?.timeZone).startOf('day').toISOString()
        }
        if (selectedDateTo) {
            tempGlobalFilters[`startDateTime.lessThan`] = momentTimezone(selectedDateTo).tz(activeRetailer?.timeZone).endOf('day').toISOString()
        }

        // Trigger update
        updateState({ page: 1, size: state.size || 20, ...tempGlobalFilters }, false)
    }

    const resetFilters = () => {
        const resetState = {
            page: 1,
            size: state.size || 20,
        }
        updateState(resetState, false)
        setLocalFilters({})
        setSelectedDateFrom(undefined)
        setSelectedDateTo(undefined)
    }

    const renderClearIcon = (slug, type: string) => {
        if (localFilters[slug]) {
            return (
                <span
                    className={`clear-icon position-absolute end-0 top-50 cursor-pointer translate-middle-y translate-y-n50 p-1 ${type === 'select' ? 'me-9' : 'px-3'
                        }`}
                    onClick={() => setLocalFilters({ ...localFilters, [slug]: '' })}
                    style={{ color: 'rgb(153, 153, 153)' }}
                >
                    <KTIcon iconName='cross' className='fs-2' />
                </span>
            )
        }
        return null
    }


    if (loading) return <CardLoading />

    if (filterData.length === 0) {
        return (
            <div className='card-body'>
                <div className='card-px text-center'>
                    <h2 className='fs-2x fw-bold mb-0'>No filter available</h2>
                    <p className='text-gray-400 fs-4 fw-semibold py-7'>
                        It looks like no filter options available
                    </p>
                </div>
            </div>
        )
    }

    return (
        <>
            <div className='row align-items-center mb-8'>
                {filterData.map((filterItem) => (
                    <div className='col-md-4 col-sm-12 col-xs-12 mb-4' key={filterItem?._id}>
                        {filterItem.slug === 'startDateTime' ? (
                            <div className='row'>
                                <div className='col-md-6 mb-2'>
                                    <label className='form-label fs-6 text-dark'>From</label>
                                    <Flatpickr
                                        options={{
                                            enableTime: false,
                                            maxDate: selectedDateTo,
                                            dateFormat: datePlaceHolder
                                                .replace('MM', 'm')
                                                .replace('DD', 'd')
                                                .replace('YYYY', 'Y'),
                                            static: true,
                                        }}
                                        placeholder={datePlaceHolder}
                                        value={selectedDateFrom}
                                        onChange={([date]) => setSelectedDateFrom(date)}
                                        className={clsx('form-control form-control-lg form-control-solid')}
                                    />
                                </div>
                                <div className='col-md-6'>
                                    <label className='form-label fs-6 text-dark'>To</label>
                                    <Flatpickr
                                        options={{
                                            enableTime: false,
                                            minDate: selectedDateFrom,
                                            dateFormat: datePlaceHolder
                                                .replace('MM', 'm')
                                                .replace('DD', 'd')
                                                .replace('YYYY', 'Y'),
                                            static: true,
                                        }}
                                        placeholder={datePlaceHolder}
                                        value={selectedDateTo}
                                        onChange={([date]) => setSelectedDateTo(date)}
                                        className={clsx('form-control form-control-lg form-control-solid')}
                                    />
                                </div>
                            </div>
                        ) : filterItem.data?.length > 0 ? (
                            <>
                                <label className='form-label fs-6 text-dark'>{filterItem.name}</label>
                                <div className="position-relative">
                                    <select
                                        className='form-select form-select-solid cursor-pointer'
                                        name={filterItem.name}
                                        value={localFilters?.[filterItem.slug] ?? ''}
                                        onChange={(e) => setLocalFilters({ ...localFilters, [filterItem.slug]: e.target.value })}
                                    >
                                        <option style={{ display: 'none' }}>Select {filterItem.name}</option>
                                        {getFilterOptionsArr(filterItem?.data).map((opt) => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                    {renderClearIcon(filterItem.slug, 'select')}
                                </div>
                            </>

                        ) : (
                            <>
                                <label className='form-label fs-6 text-dark'>{filterItem.name}</label>
                                <div className="position-relative">
                                    <input
                                        className='form-control form-control-solid'
                                        placeholder={filterItem.name}
                                        name={filterItem.name}
                                        value={localFilters?.[filterItem.slug] ?? ''}
                                        onChange={(e) => setLocalFilters({ ...localFilters, [filterItem.slug]: e.target.value })}
                                        autoComplete='off'
                                    />
                                    {renderClearIcon(filterItem.slug, 'text')}
                                </div>
                            </>
                        )}
                    </div>
                ))}
            </div>

            <div className='row gap-5 justify-content-end'>
                <button className={'col-md-2 btn btn-light'} onClick={resetFilters}>
                    <span className='indicator-label'>{intl.formatMessage({ id: 'RESET' })}</span>
                </button>
                <button
                    className={'col-md-2 btn btn-primary'}
                    style={{ height: '100%', minHeight: 'min-content' }}
                    onClick={applyFilters}
                >
                    {!isLoading && <span className='indicator-label'>{intl.formatMessage({ id: 'SEARCH' })}</span>}
                    {isLoading && (
                        <span className='indicator-progress' style={{ display: 'block' }}>
                            Please wait...
                            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                        </span>
                    )}
                </button>
            </div>
        </>
    )
}

export default PaymentSchemesFilterComp
